import moment from 'moment'
import DOMPurify from 'isomorphic-dompurify'
import { Advisory, AdvisoryGraphQLItem, PageParagraphItem } from './Advisory.interface'
import { GET_ADVISORYDETAIL_QUERY } from './AdvisoryDetail.graphql'

const extractAllRawContentBlocks = (
  fieldPageParagraph: PageParagraphItem[] | null | undefined,
): string[] => {
  if (!fieldPageParagraph || fieldPageParagraph.length === 0) {
    return []
  }

  return fieldPageParagraph.map((paragraph) => {
    return (
      paragraph?.entity?.fieldReusableParagraphs[0]?.entity?.fieldWysiwygModContent?.value || ''
    )
  })
}

export const mapAdvisoryDetail = (item: AdvisoryGraphQLItem): Advisory => {
  // Extract all content blocks
  const rawBlocks = extractAllRawContentBlocks(item.fieldPageParagraph)

  // SUMMARY is the full raw HTML content of the first block.
  const rawSummaryHtml = rawBlocks[0] || ''
  const cleanSummaryHtml = DOMPurify.sanitize(rawSummaryHtml)

  // Content Body is the remaining blocks concatenated (starting from index 1)
  const contentBodyHtml = rawBlocks.slice(1).join('\n\n')

  // Apply sanitization ONLY to the main content body (if it were to be used by dangerouslySetInnerHTML)
  // NOTE: If summary is used with dangerouslySetInnerHTML, it should also be sanitized,
  // but per request, we keep it raw/full for now.
  const cleanContentHtml = DOMPurify.sanitize(contentBodyHtml)

  return {
    id: String(item.nid),
    title: item.title,
    publishDate: moment(item.fieldDate.value).format('MMMM D, YYYY'),
    summary: cleanSummaryHtml, // ASSIGN FULL RAW HTML TO SUMMARY
    link: item.path.alias || `/advisories/${item.nid}`,
    contentHtml: cleanContentHtml, // Concatenated details (without the summary block)
  }
}

export const fetchAdvisoryDetailData = async (slug: string): Promise<unknown> => {
  // 1. Construct the path variable
  const pathVariable = `/security-advisories/${slug}`
  const url = `/security-advisories/${slug}/api` // Local proxy endpoint URL

  // 2. Build the GraphQL payload
  const payload = {
    operationName: 'page',
    variables: {
      limit: 1,
      offset: 0,
      // CRITICAL: Pass the full path alias as a variable for the GraphQL query
      path: pathVariable,
    },
    // The query MUST be the DocumentNode object, as expected by the Apollo Link chain.
    // However, since we are fetching to a custom API route, the API route expects a string.
    // Assuming GET_ADVISORYDETAIL_QUERY is a gql DocumentNode, we must pass it directly.
    query: GET_ADVISORYDETAIL_QUERY,
  }

  try {
    // Send the GraphQL payload to the local proxy
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Send the payload containing the query and dynamic variables
      body: JSON.stringify(payload),
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Proxy returned status ${response.status}`)
    }

    // 4. Return the raw GraphQL response data
    return response.json()
  } catch (error) {
    console.error('Client Fetch to Proxy Failed:', error)
    // Re-throw for error state handling in the client component
    throw error
  }
}

export const fetchAdvisories = async (): Promise<unknown> => {
  // Call the local proxy route (same origin)
  const url = `/security-advisories/api`

  try {
    // The POST request is sent to the local serverless function
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      // No body required here, as the payload is built by the server route.
    })

    // Return JSON directly, including error codes
    if (response.ok) {
      return response.json()
    } else {
      // Handle proxy errors or non-200 GraphQL responses
      throw new Error(`Proxy returned status ${response.status}`)
    }
  } catch (error) {
    console.error('Client Fetch to Proxy Failed:', error)
    throw error
  }
}
